import React from 'react';
import ValidationElement from 'components/Form/ValidationElement';
import Field from 'components/Form/Field';
import { i18n } from 'config/locales';
import Name from 'components/Form/Name';
import Telephone from 'components/Form/Telephone';
import DateRange from 'components/Form/DateRange';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';
import Text from 'components/Form/Text';
import Location from 'components/Form/Location';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import { Dispatch } from 'redux';
import { SubstanceDrugsVoluntaryTreatmentItemModel } from 'forms/sections/substance/drugs/voluntaryTreatment/types';
import Comments from 'components/Form/Comments';
import { isPVQForm } from 'helpers/form';
import Checkbox from 'components/Form/Checkbox';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import PVQName from 'components/Form/PVQName';
import PVQLocation from 'components/Form/PVQLocation';
import EmailCollection from 'components/Form/PVQEmail/EmailCollection';
import TelephoneCollection from 'components/Form/Telephone/TelephoneCollection';
import NotApplicable from 'components/Form/NotApplicable';
import { CheckboxModel } from 'forms/shared/types';
import LocationUnknown from 'components/Form/PVQLocation/LocationUnknown';
import DrugTypes from './DrugTypes';
import DrugType from './DrugType';

interface Props extends Partial<SubstanceDrugsVoluntaryTreatmentItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
  formType?: string
}

export default class VoluntaryTreatment extends ValidationElement<Props> {
  handleNameError = (value: any, arr: ErrorCode[]) => {
    const arr2 = arr.map((err) => ({
      ...err,
      code: err.code.includes('required')
        ? `name${err.code.substring(err.code.lastIndexOf('.'))}`
        : err.code,
    }));
    return this.props.onError(value, arr2);
  };

  update = (updateValues: Partial<SubstanceDrugsVoluntaryTreatmentItemModel>) => {
    if (this.props.onUpdate) {
      this.props.onUpdate({
        DrugType: this.props.DrugType,
        TreatmentProvider: this.props.TreatmentProvider,
        TreatmentProviderNotApplicable: this.props.TreatmentProviderNotApplicable,
        TreatmentProviderAddress: this.props.TreatmentProviderAddress,
        TreatmentProviderTelephone: this.props.TreatmentProviderTelephone,
        TreatmentDates: this.props.TreatmentDates,
        TreatmentCompleted: this.props.TreatmentCompleted,
        NoTreatmentExplanation: this.props.NoTreatmentExplanation,
        Comments: this.props.Comments,
        NoTreatmentComments: this.props.NoTreatmentComments,
        DueToDrugs: this.props.DueToDrugs,
        DrugTypeOther: this.props.DrugTypeOther,
        DrugTypes: this.props.DrugTypes,
        DueToPrescription: this.props.DueToPrescription,
        PrescriptionDrug: this.props.PrescriptionDrug,
        TreatmentProviderInPractice: this.props.TreatmentProviderInPractice,
        TreatmentProviderPracticeName: this.props.TreatmentProviderPracticeName,
        TreatmentProviderPracticeNameNotApplicable: this.props.TreatmentProviderPracticeNameNotApplicable,
        TreatmentProviderTelephones: this.props.TreatmentProviderTelephones,
        TreatmentProviderEmails: this.props.TreatmentProviderEmails,
        TreatmentStatus: this.props.TreatmentStatus,
        RecommendedBy: this.props.RecommendedBy,
        RecommendedOther: this.props.RecommendedOther,
        ...updateValues,
      });
    }
  };

  updateDrugType = (values: SubstanceDrugsVoluntaryTreatmentItemModel['DrugType']) => {
    this.update({ DrugType: values });
  };

  updateTreatmentProvider = (
    values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentProvider']
  ) => {
    this.update({ TreatmentProvider: values });
  };

  updateTreatmentProviderNotApplicable = (values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentProviderNotApplicable']) => {
    this.update({
      TreatmentProviderNotApplicable: values,
      TreatmentProvider: values?.applicable !== false ? this.props.TreatmentProvider : {} as SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentProvider'],
    });
  };

  updateTreatmentProviderPracticeName = (values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentProviderPracticeName']) => {
    this.update({ TreatmentProviderPracticeName: values });
  };

  updateTreatmentProviderAddress = (
    values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentProviderAddress']
  ) => {
    this.update({ TreatmentProviderAddress: values });
  };

  updateTreatmentProviderTelephone = (
    values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentProviderTelephone']
  ) => {
    this.update({ TreatmentProviderTelephone: values });
  };

  updateTreatmentDates = (values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentDates']) => {
    this.update({ TreatmentDates: values });
  };

  updateTreatmentCompleted = (
    values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentCompleted']
  ) => {
    this.update({ TreatmentCompleted: values });
  };

  updateNoTreatmentExplanation = (
    values: SubstanceDrugsVoluntaryTreatmentItemModel['NoTreatmentExplanation']
  ) => {
    this.update({ NoTreatmentExplanation: values });
  };

  updateComments = (
    values: SubstanceDrugsVoluntaryTreatmentItemModel['Comments']
  ) => {
    this.update({ Comments: values });
  };

  updateNoTreatmentComments = (
    values: SubstanceDrugsVoluntaryTreatmentItemModel['NoTreatmentComments']
  ) => {
    this.update({ NoTreatmentComments: values });
  };

  updateDueToDrugs = (values: SubstanceDrugsVoluntaryTreatmentItemModel['DueToDrugs']) => {
    this.update({ DueToDrugs: values });
  };

  updateDrugTypes = (values: SubstanceDrugsVoluntaryTreatmentItemModel['DrugTypes']) => {
    this.update({ DrugTypes: values });
  };

  updateDrugTypeOther = (values: SubstanceDrugsVoluntaryTreatmentItemModel['DrugTypeOther']) => {
    this.update({ DrugTypeOther: values });
  };

  updateDueToPrescription = (values: SubstanceDrugsVoluntaryTreatmentItemModel['DueToPrescription']) => {
    this.update({ DueToPrescription: values });
  };

  updatePrescriptionDrug = (values: SubstanceDrugsVoluntaryTreatmentItemModel['PrescriptionDrug']) => {
    this.update({ PrescriptionDrug: values });
  };

  updateTreatmentProviderInPractice = (values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentProviderInPractice']) => {
    this.update({ TreatmentProviderInPractice: values });
  };

  updateTreatmentProviderTelephones = (values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentProviderTelephones']) => {
    this.update({ TreatmentProviderTelephones: values });
  };

  updateTreatmentProviderEmails = (values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentProviderEmails']) => {
    this.update({ TreatmentProviderEmails: values });
  };

  updateTreatmentStatus = (values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentStatus']) => {
    this.update({ TreatmentStatus: values });
  };

  getRecommendedBy = (cb: CheckboxModel) => {
    const selected = cb?.value;
    let list = [...(this.props.RecommendedBy?.values || [])];

    if (list.includes(selected)) {
      list.splice(list.indexOf(selected), 1);
    } else if (cb?.checked && (selected === 'None' || list.includes('None') || selected === 'No' || list.includes('No'))) {
      list = [selected];
    } else {
      list.push(selected);
    }
    return list;
  };

  updateRecommendedBy = (cb: CheckboxModel) => {
    const values = this.getRecommendedBy(cb);
    this.update({ RecommendedBy: { values } });
  };

  updateRecommendedOther = (values: SubstanceDrugsVoluntaryTreatmentItemModel['RecommendedOther']) => {
    this.update({ RecommendedOther: values });
  };

  updateTreatmentProviderPracticeNameNotApplicable = (values: SubstanceDrugsVoluntaryTreatmentItemModel['TreatmentProviderPracticeNameNotApplicable']) => {
    this.update({ TreatmentProviderPracticeNameNotApplicable: values });
  };

  /* PVQ Updates to SubstanceDrugsVoluntaryTreatmentItemModel
  RecommendedBy?: CheckboxGroupModel
  RecommendedOther?: FieldModel
  DueToDrugs?: YesOrNoModel
  DrugTypeOther?: FieldModel
  DrugTypes?: CheckboxGroupModel
  DueToPrescription?: YesOrNoModel
  PrescriptionDrug?: FieldModel
   TreatmentProviderInPractice?: YesOrNoModel
   TreatmentProviderPracticeName?: FieldModel
     TreatmentProviderPracticeNameNotApplicable?: ApplicableModel
  TreatmentProviderTelephones?: BranchCollectionModel<TelephoneItemModel>
  TreatmentProviderEmails?: BranchCollectionModel<EmailItemModel>
  TreatmentStatus?: FieldModel
*/
  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPvq = isPVQForm(this.props.formType);
    return isPvq ? (
      <div className="drug-voluntary-treatment">
        <Field
          title={i18n.t('substance.pvq.drugs.voluntary.heading.recommendedBy')}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.RecommendedBy`}
        >
          <CheckboxGroup
            className="ordered-by option-list option-list-vertical"
            required={this.props.required}
            onError={this.props.onError}
            selectedValues={(this.props.RecommendedBy || {}).values}
            attribute={`${attribute}.RecommendedBy`}
          >
            <Checkbox
              name="Employer"
              label="Employer"
              value="Employer"
              className="employer"
              onUpdate={this.updateRecommendedBy}
              onError={this.props.onError}
            />
            <Checkbox
              name="MilitaryCommander"
              label="Military Commander"
              value="Military Commander"
              className="military-commander"
              onUpdate={this.updateRecommendedBy}
              onError={this.props.onError}
            />
            <Checkbox
              name="MedicalProfessional"
              label="Medical Professional"
              value="Medical Professional"
              className="medical-professional"
              onUpdate={this.updateRecommendedBy}
              onError={this.props.onError}
            />
            <Checkbox
              name="MentalHealthProfessional"
              label="Mental Health Professional"
              value="Mental Health Professional"
              className="mental-health-professional"
              onUpdate={this.updateRecommendedBy}
              onError={this.props.onError}
            />
            <Checkbox
              name="Employee Assistance Program"
              label="Employee Assistance Program"
              value="Employee Assistance Program"
              className="employee-assistance-program"
              onUpdate={this.updateRecommendedBy}
              onError={this.props.onError}
            />
            <Checkbox
              name="Other"
              label="Other"
              value="Other"
              className="other"
              onUpdate={this.updateRecommendedBy}
              onError={this.props.onError}
            />
            <Checkbox
              name="No"
              label="No"
              value="No"
              className="no"
              onUpdate={this.updateRecommendedBy}
              onError={this.props.onError}
            />
          </CheckboxGroup>
        </Field>
        {this.props.RecommendedBy?.values?.includes('Other') && (
          <Field
            title={i18n.t('substance.pvq.drugs.voluntary.heading.explanation')}
            attribute={`${attribute}.RecommendedOther`}
            scrollIntoView={this.props.scrollIntoView}
          >
            <Textarea
              {...this.props.RecommendedOther}
              name="RecommendedOther"
              attribute={`${attribute}.RecommendedOther`}
              className="recommendedOther"
              onUpdate={this.updateRecommendedOther}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>
        )}

        <Branch
          {...this.props.DueToPrescription}
          name="DueToPrescription"
          label={i18n.t('substance.pvq.drugs.voluntary.heading.dueToPrescription')}
          labelSize="h4"
          className="dueToPrescription"

          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateDueToPrescription}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DueToPrescription`}
          warning
        />
        {this.props.DueToPrescription?.value === 'Yes' && (
          <Field
            title={i18n.t('substance.pvq.drugs.voluntary.heading.prescriptionDrug')}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.PrescriptionDrug`}
          >
            <Text
              {...this.props.PrescriptionDrug}
              name="PrescriptionDrug"
              className="prescriptionDrug"
              onUpdate={this.updatePrescriptionDrug}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.PrescriptionDrug`}
            />
          </Field>
        )}
        <Branch
          {...this.props.DueToDrugs}
          name="DueToDrugs"
          label={i18n.t('substance.pvq.drugs.voluntary.heading.dueToDrugs')}
          labelSize="h4"
          className="dueToDrugs"
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateDueToDrugs}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DueToDrugs`}
          warning
        />
        {this.props.DueToDrugs?.value === 'Yes' && (
          <>
            <Field
              title={i18n.t('substance.pvq.drugs.voluntary.heading.drugTypes')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.DrugTypes`}
            >
              <DrugTypes
                {...this.props.DrugTypes}
                onUpdate={this.updateDrugTypes}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.DrugTypes`}
              />
            </Field>

            {this.props.DrugTypes?.values?.includes('Other') && (
              <Field
                title={i18n.t('substance.pvq.drugs.use.heading.otherDrugType')}
                attribute={`${attribute}.DrugTypeOther`}
                scrollIntoView={this.props.scrollIntoView}
              >
                <Textarea
                  {...this.props.DrugTypeOther}
                  name="DrugTypeOther"
                  attribute={`${attribute}.DrugTypeOther`}
                  className="drugTypeOther"
                  onUpdate={this.updateDrugTypeOther}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
            )}
          </>
        )}
        <Field
          title={i18n.t('substance.pvq.drugs.voluntary.heading.treatmentDates')}
          adjustFor="daterange"
          help="substance.drugs.voluntary.help.treatmentDates"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TreatmentDates`}
        >
          <DateRange
            name="TreatmentDates"
            className="treatment-dates"
            {...this.props.TreatmentDates}
            minDateEqualTo
            onUpdate={this.updateTreatmentDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.TreatmentDates`}
          />
        </Field>

        <Field
          title={i18n.t('substance.pvq.drugs.voluntary.heading.treatmentProvider')}
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <NotApplicable
            name="TreatmentProviderNotApplicable"
            {...this.props.TreatmentProviderNotApplicable}
            label={i18n.t('substance.pvq.drugs.voluntary.heading.treatmentProvideridk')}
            or={i18n.t('substance.pvq.drugs.voluntary.para.or')}
            onUpdate={this.updateTreatmentProviderNotApplicable}
            attribute={`${attribute}.TreatmentProviderNotApplicable`}
            includeNAConfirmation={Boolean(this.props.TreatmentProvider?.value)}
          >
            <PVQName
              {...this.props.TreatmentProvider}
              aria-label="Provide the name of the treatment provider"
              attribute={`${attribute}.TreatmentProvider`}
              className="treatment-provider"
              exactAttribute
              hideMiddleName
              showLastFirstMiddleNameOrder
              name="TreatmentProvider"
              onError={this.props.onError} // eslint-disable-line @typescript-eslint/unbound-method
              onUpdate={this.updateTreatmentProvider}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </NotApplicable>
        </Field>

        <Field
          title={i18n.t('substance.pvq.drugs.voluntary.heading.treatmentProviderPracticeName')}
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TreatmentProviderPracticeName`}
        >
          <NotApplicable
            name="TreatmentProviderPracticeNameNotApplicable"
            {...this.props.TreatmentProviderPracticeNameNotApplicable}
            label={i18n.t('substance.pvq.drugs.voluntary.label.idk')}
            or={i18n.t('substance.pvq.drugs.voluntary.para.or')}
            onUpdate={this.updateTreatmentProviderPracticeNameNotApplicable}
            attribute={`${attribute}.TreatmentProviderPracticeNameNotApplicable`}
            includeNAConfirmation={Boolean(this.props.TreatmentProviderPracticeName?.value)}
          >
            <Text
              {...this.props.TreatmentProviderPracticeName}
              name="treatmentProviderPracticeName"
              className="treatmentProviderPracticeName"
              onUpdate={this.updateTreatmentProviderPracticeName}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.TreatmentProviderPracticeName`}
            />
          </NotApplicable>
        </Field>

        <Branch
          {...this.props.TreatmentProviderInPractice}
          name="TreatmentProviderInPractice"
          label={i18n.t('substance.pvq.drugs.voluntary.heading.treatmentProviderInPractice')}
          labelSize="h4"
          className="action-taken"
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateTreatmentProviderInPractice}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TreatmentProviderInPractice`}
          showDNK
          warning
        />

        <TelephoneCollection
          {...this.props.TreatmentProviderTelephones}
          onUpdate={this.updateTreatmentProviderTelephones}
          onError={this.props.onError}
          required={this.props.required}
          showTimeOfDay
          hideInitialBranchQuestion
          allowNotApplicable
          attribute={`${attribute}.TreatmentProviderTelephones`}
          telephoneFieldTitle={i18n.t('substance.pvq.drugs.voluntary.heading.phoneNumbers.title')}
          appendFieldTitle={i18n.t('substance.pvq.drugs.voluntary.heading.phoneNumbers.append')}
          branchFieldTitle={i18n.t('substance.pvq.drugs.voluntary.heading.phoneNumbers.branch.title')}
        />
        <EmailCollection
          {...this.props.TreatmentProviderEmails}
          onUpdate={this.updateTreatmentProviderEmails}
          onError={this.props.onError}
          required={this.props.required}
          includeEmailType={false}
          attribute={`${attribute}.TreatmentProviderEmails`}
          emailFieldTitle={i18n.t('substance.pvq.drugs.voluntary.heading.emails.title')}
          appendFieldTitle={i18n.t('substance.pvq.drugs.voluntary.heading.emails.append')}
          branchFieldTitle={i18n.t('substance.pvq.drugs.voluntary.heading.emails.branch.title')}
        />
        <Field
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TreatmentProviderAddress`}
          adjustFor="address"
          exactAttribute
        >
          <LocationUnknown
            {...this.props.TreatmentProviderAddress}
            addressBook="Provider"
            addressBooks={this.props.addressBooks}
            attribute={`${attribute}.TreatmentProviderAddress`}
            className="treatment-provider-address"
            dispatch={this.props.dispatch}
            domesticTitle={i18n.t(`substance.pvq.drugs.voluntary.heading.treatmentProviderAddressDomesticTitle`)}
            foreignTitle={i18n.t(`substance.pvq.drugs.voluntary.heading.treatmentProviderAddressForeignTitle`)}
            geocode
            includeMilitaryStates
            isPoBoxAllowed={false}
            label={i18n.t('substance.pvq.drugs.voluntary.heading.treatmentProviderAddress')}
            name="TreatmentProviderAddress"
            onError={this.props.onError}
            onUpdate={this.updateTreatmentProviderAddress}
            required={this.props.required}
            showInternationalStreet={false}
            showMilitaryInstallation
            exactAttribute
          />
        </Field>
        <Branch
          name="TreatmentCompleted"
          label={i18n.t('substance.pvq.drugs.voluntary.heading.treatmentCompleted')}
          labelSize="h4"
          className="treatment-completed no-margin-bottom"
          {...this.props.TreatmentCompleted}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateTreatmentCompleted}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TreatmentCompleted`}
        />

        {this.props.TreatmentCompleted?.value === 'No' && (
          <>
            <Field
              title={i18n.t('substance.pvq.drugs.voluntary.heading.noTreatmentExplanation')}
              titleSize="label"
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.NoTreatmentExplanation`}
            >
              <Textarea
                {...this.props.NoTreatmentExplanation}
                name="NoTreatmentExplanation"
                className="no-treatment-explanation"
                onUpdate={this.updateNoTreatmentExplanation}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.NoTreatmentExplanation`}
              />
            </Field>
            <Field
              title={i18n.t('substance.pvq.drugs.voluntary.heading.treatmentStatus')}
              scrollIntoView={this.props.scrollIntoView}
              attribute={`${attribute}.TreatmentStatus`}
            >
              <Textarea
                {...this.props.TreatmentStatus}
                name="TreatmentStatus"
                className="treatmentStatus"
                onUpdate={this.updateTreatmentStatus}
                onError={this.props.onError}
                required={this.props.required}
                attribute={`${attribute}.TreatmentStatus`}
              />
            </Field>
          </>
        )}

      </div>

    ) : (
      <div className="drug-voluntary-treatment">
        <Field
          title={i18n.t('substance.drugs.voluntary.heading.drugType')}
          className="drug-type-voluntary"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.DrugType`}
        >
          <DrugType
            name="DrugType"
            {...this.props.DrugType}
            onUpdate={this.updateDrugType}
            onError={this.props.onError}
            required={this.props.required}
            attribute={attribute}
          />
        </Field>

        <Field
          title={i18n.t('substance.drugs.voluntary.heading.treatmentProvider')}
          adjustFor="label"
          filterErrors={Name.requiredErrorsOnly}
          scrollIntoView={this.props.scrollIntoView}
        >
          <Name
            name="TreatmentProvider"
            prefix="name"
            className="treatment-provider"
            aria-label="Provide the name of the treatment provider"
            {...this.props.TreatmentProvider}
            scrollIntoView={this.props.scrollIntoView}
            hideMiddleName
            onUpdate={this.updateTreatmentProvider}
            onError={this.handleNameError}
            required={this.props.required}
            attribute={`${attribute}.TreatmentProvider`}
          />
        </Field>

        <Field
          title={i18n.t('substance.drugs.voluntary.heading.treatmentProviderAddress')}
          optional
          adjustFor="address"
          help="substance.drugs.voluntary.help.treatmentProviderAddress"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TreatmentProviderAddress`}
        >
          <Location
            name="TreatmentProviderAddress"
            className="treatment-provider-address"
            {...this.props.TreatmentProviderAddress}
            label={i18n.t('reference.label.address')}
            layout={Location.ADDRESS}
            geocode
            addressBooks={this.props.addressBooks}
            addressBook="Provider"
            showPostOffice
            dispatch={this.props.dispatch}
            onUpdate={this.updateTreatmentProviderAddress}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.TreatmentProviderAddress`}
          />
        </Field>
        <Field
          title={i18n.t('substance.drugs.voluntary.heading.treatmentProviderTelephone')}
          className="treatment-provider-telephone override-required"
          help="substance.drugs.voluntary.help.treatmentProviderTelephone"
          adjustFor="telephone"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TreatmentProviderTelephone`}
        >
          <Telephone
            name="TreatmentProviderTelephone"
            {...this.props.TreatmentProviderTelephone}
            allowNotApplicable={false}
            onUpdate={this.updateTreatmentProviderTelephone}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.TreatmentProviderTelephone`}
          />
        </Field>

        <Field
          title={i18n.t('substance.drugs.voluntary.heading.treatmentDates')}
          adjustFor="daterange"
          help="substance.drugs.voluntary.help.treatmentDates"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TreatmentDates`}
        >
          <DateRange
            name="TreatmentDates"
            className="treatment-dates"
            {...this.props.TreatmentDates}
            minDateEqualTo
            onUpdate={this.updateTreatmentDates}
            onError={this.props.onError}
            required={this.props.required}
            hideFromDay
            hideToDay
            attribute={`${attribute}.TreatmentDates`}
          />
        </Field>

        <Branch
          name="TreatmentCompleted"
          label={i18n.t('substance.drugs.voluntary.heading.treatmentCompleted')}
          labelSize="h4"
          className="treatment-completed"
          {...this.props.TreatmentCompleted}
          onError={this.props.onError}
          required={this.props.required}
          onUpdate={this.updateTreatmentCompleted}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.TreatmentCompleted`}
        />

        <Show when={this.props.TreatmentCompleted?.value === 'No'}>
          <Field
            title={i18n.t('substance.drugs.voluntary.heading.noTreatmentExplanation')}
            titleSize="label"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.NoTreatmentExplanation`}
          >
            <Textarea
              name="NoTreatmentExplanation"
              className="no-treatment-explanation"
              {...this.props.NoTreatmentExplanation}
              onUpdate={this.updateNoTreatmentExplanation}
              onError={this.props.onError}
              required={this.props.required}
              attribute={`${attribute}.NoTreatmentExplanation`}
            />
          </Field>
          <Comments
            title={i18n.t('substance.comments.optional')}
            name="NoTreatmentComments"
            className="optional-comment"
            attribute="NoTreatmentComments"
            {...this.props.NoTreatmentComments}
            onUpdate={this.updateNoTreatmentComments}
            required={false}
            negativeMarginTop={false}
          />
        </Show>
        <Comments
          title={i18n.t('substance.comments.summary')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
      </div>
    );
  }
}

VoluntaryTreatment.defaultProps = {
  TreatmentCompleted: {},
  addressBooks: {},
  Comments: {},
  NoTreatmentComments: {},
  DueToDrugs: {},
  DrugTypes: {},
  DrugTypeOther: {},
  DueToPrescription: {},
  PrescriptionDrug: {},
  TreatmentProviderInPractice: {},
  TreatmentProviderTelephones: {},
  TreatmentProviderEmails: {},
  TreatmentStatus: {},
  RecommendedBy: {},
  OrderedBRecommendedByOther: {},
  dispatch: (action) => { },
  onError: (value, arr: ErrorCode) => arr,
};
